import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAccounts } from "@/contexts/AccountContext";
import { createAccount, updateAccount, deleteAccount } from "@/lib/api";
import { toast } from "sonner";
import { SETTINGS } from "@/constants/testIds";

export default function Settings() {
  const { accounts, refresh } = useAccounts();
  const [editing, setEditing] = useState({}); // id -> partial state
  const [creating, setCreating] = useState(false);
  const [newAcc, setNewAcc] = useState({ name: "", account_number: "", monthly_budget_rm: 0, declared_load_kw: 0 });

  useEffect(() => {
    const draft = {};
    accounts.forEach((a) => {
      draft[a.account_id] = {
        name: a.name,
        account_number: a.account_number || "",
        monthly_budget_rm: a.monthly_budget_rm || 0,
        declared_load_kw: a.declared_load_kw || 0,
        share_enabled: !!a.share_enabled,
      };
    });
    setEditing(draft);
  }, [accounts]);

  const patch = (id, key, val) =>
    setEditing((prev) => ({ ...prev, [id]: { ...prev[id], [key]: val } }));

  const save = async (id) => {
    try {
      const payload = editing[id];
      await updateAccount(id, {
        name: payload.name,
        account_number: payload.account_number,
        monthly_budget_rm: Number(payload.monthly_budget_rm),
        declared_load_kw: Number(payload.declared_load_kw) || 0,
      });
      toast.success("Account saved");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const toggleShare = async (id, next) => {
    try {
      const acc = await updateAccount(id, { share_enabled: next });
      toast.success(next ? "Public snapshot enabled" : "Public snapshot disabled");
      patch(id, "share_enabled", acc.share_enabled);
      refresh();
    } catch (e) {
      toast.error("Failed to update share");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this account AND all its bills?")) return;
    try {
      await deleteAccount(id);
      toast.success("Account deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newAcc.name.trim()) return toast.error("Name required");
    try {
      await createAccount({
        name: newAcc.name,
        account_number: newAcc.account_number,
        monthly_budget_rm: Number(newAcc.monthly_budget_rm) || 0,
        declared_load_kw: Number(newAcc.declared_load_kw) || 0,
      });
      toast.success("Account created");
      setCreating(false);
      setNewAcc({ name: "", account_number: "", monthly_budget_rm: 0, declared_load_kw: 0 });
      refresh();
    } catch (e) {
      toast.error("Failed to create account");
    }
  };

  return (
    <Layout>
      <div data-testid={SETTINGS.page} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-10 flex justify-between items-end flex-wrap gap-4">
          <div>
            <div className="overline mb-3">Configuration</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Accounts &amp; sharing.</h1>
            <p className="mt-3 text-[#6E716C] max-w-xl">
              Group bills by TNB account, set a monthly budget, and enable a public read-only snapshot for sharing.
            </p>
          </div>
          <button
            data-testid={SETTINGS.addAccountBtn}
            onClick={() => setCreating((s) => !s)}
            className="px-5 py-3 bg-[#0F3524] text-[#F7F7F4] font-medium hover:bg-[#174D35] transition-colors"
          >
            {creating ? "Cancel" : "+ New account"}
          </button>
        </div>

        {creating && (
          <form onSubmit={create} className="flat-card p-6 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
            <FieldInput label="Name" value={newAcc.name} onChange={(v) => setNewAcc((s) => ({ ...s, name: v }))} placeholder="Home, Office, …" />
            <FieldInput label="TNB account number" value={newAcc.account_number} onChange={(v) => setNewAcc((s) => ({ ...s, account_number: v }))} placeholder="Optional" />
            <FieldInput label="Monthly budget (RM)" type="number" step="0.01" value={newAcc.monthly_budget_rm} onChange={(v) => setNewAcc((s) => ({ ...s, monthly_budget_rm: v }))} />
            <FieldInput label="Declared load (kW)" type="number" step="0.1" value={newAcc.declared_load_kw} onChange={(v) => setNewAcc((s) => ({ ...s, declared_load_kw: v }))} placeholder="Commercial only" />
            <div className="sm:col-span-4">
              <button type="submit" className="px-5 py-3 bg-[#0F3524] text-[#F7F7F4] hover:bg-[#174D35] transition-colors">
                Create account
              </button>
            </div>
          </form>
        )}

        <div className="space-y-6">
          {accounts.map((a) => {
            const e = editing[a.account_id] || {};
            const shareUrl = a.share_id ? `${window.location.origin}/s/${a.share_id}` : "";
            return (
              <div key={a.account_id} data-testid={SETTINGS.accountRow(a.account_id)} className="flat-card p-6 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FieldInput
                    label="Account name"
                    testid={SETTINGS.nameInput(a.account_id)}
                    value={e.name || ""}
                    onChange={(v) => patch(a.account_id, "name", v)}
                  />
                  <FieldInput
                    label="TNB account number"
                    value={e.account_number || ""}
                    onChange={(v) => patch(a.account_id, "account_number", v)}
                  />
                  <FieldInput
                    label="Monthly budget (RM)"
                    testid={SETTINGS.budgetInput(a.account_id)}
                    type="number"
                    step="0.01"
                    value={e.monthly_budget_rm ?? 0}
                    onChange={(v) => patch(a.account_id, "monthly_budget_rm", v)}
                  />
                  <FieldInput
                    label="Declared load (kW)"
                    testid={`settings-declared-${a.account_id}`}
                    type="number"
                    step="0.1"
                    value={e.declared_load_kw ?? 0}
                    onChange={(v) => patch(a.account_id, "declared_load_kw", v)}
                    placeholder="Commercial only"
                  />
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <button
                    data-testid={SETTINGS.saveBtn(a.account_id)}
                    onClick={() => save(a.account_id)}
                    className="px-5 py-2.5 bg-[#0F3524] text-[#F7F7F4] text-sm font-medium hover:bg-[#174D35] transition-colors"
                  >
                    Save
                  </button>
                  <button
                    data-testid={SETTINGS.deleteBtn(a.account_id)}
                    onClick={() => remove(a.account_id)}
                    className="px-5 py-2.5 border border-[#E5E5DE] text-sm hover:border-[#B24F35] hover:text-[#B24F35] transition-colors"
                  >
                    Delete
                  </button>
                </div>

                <div className="mt-8 border-t border-[#E5E5DE] pt-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="overline mb-2">Public snapshot</div>
                      <p className="text-sm text-[#6E716C] max-w-md">
                        Enable a read-only page anyone can view. Great for sharing your carbon footprint on socials.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        data-testid={SETTINGS.shareToggle(a.account_id)}
                        type="checkbox"
                        checked={!!a.share_enabled}
                        onChange={(ev) => toggleShare(a.account_id, ev.target.checked)}
                        className="w-4 h-4 accent-[#0F3524]"
                      />
                      <span className="text-sm font-medium">{a.share_enabled ? "Enabled" : "Disabled"}</span>
                    </label>
                  </div>
                  {a.share_enabled && shareUrl && (
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <code className="font-mono-data text-xs bg-[#F0F0EA] px-3 py-2 border border-[#E5E5DE] break-all">
                        {shareUrl}
                      </code>
                      <button
                        data-testid={SETTINGS.copyShareBtn(a.account_id)}
                        onClick={() => {
                          navigator.clipboard.writeText(shareUrl);
                          toast.success("Link copied");
                        }}
                        className="text-xs font-mono-data uppercase tracking-widest border border-[#E5E5DE] px-3 py-2 hover:border-[#0F3524] transition-colors"
                      >
                        Copy link
                      </button>
                      <a
                        href={shareUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-mono-data uppercase tracking-widest border border-[#E5E5DE] px-3 py-2 hover:border-[#0F3524] transition-colors"
                      >
                        Open
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}

function FieldInput({ label, testid, type = "text", value, onChange, step, ...rest }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        className="w-full bg-white border border-[#E5E5DE] px-4 py-3 font-mono-data text-sm focus:outline-none focus:border-[#0F3524]"
        {...rest}
      />
    </label>
  );
}
